import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { TimeSeriesData } from '@/types';

interface AnalyticsChartProps {
  data: TimeSeriesData[];
  showVisitors?: boolean;
  showBots?: boolean;
  height?: number;
  timezone?: string;
  isLoading?: boolean;
}

const SERIES_LABELS: Record<string, string> = {
  pageviews: 'Pageviews',
  visitors: 'Visitors',
  crawlers: 'Crawlers',
  bots: 'Bots',
};

export function AnalyticsChart({
  data,
  showVisitors = true,
  showBots = true,
  height = 300,
  timezone = 'UTC',
  isLoading = false,
}: AnalyticsChartProps) {
  // Only draw the crawler / bot series if there is actually that traffic to show.
  const hasCrawlers = showBots && data.some((item) => (item.crawlers ?? 0) > 0);
  const hasBots = showBots && data.some((item) => (item.bots ?? 0) > 0);
  // Detect if this is hourly data by checking if consecutive dates are hours apart
  const isHourly = data.length >= 2 && (() => {
    const d1Str = data[0].date.endsWith('Z') ? data[0].date : data[0].date + 'Z';
    const d2Str = data[1].date.endsWith('Z') ? data[1].date : data[1].date + 'Z';
    const d1 = new Date(d1Str);
    const d2 = new Date(d2Str);
    const diffHours = Math.abs(d2.getTime() - d1.getTime()) / (1000 * 60 * 60);
    return diffHours <= 1;
  })();

  const formattedData = data.map((item) => {
    // Backend returns UTC timestamps without 'Z' suffix, so append it
    const dateStr = item.date.endsWith('Z') ? item.date : item.date + 'Z';
    const date = new Date(dateStr);
    return {
      ...item,
      date: isHourly
        ? date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true, timeZone: timezone })
        : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone }),
    };
  });

  const formatNumber = (value: number): string => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  if (isLoading || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500"
        style={{ height }}
      >
        {isLoading ? (
          <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          'No data available for this period'
        )}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatNumber}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
          formatter={(value: number, name: string) => [
            value.toLocaleString(),
            SERIES_LABELS[name] ?? name,
          ]}
        />
        <Legend />
        <Area
          type="monotone"
          dataKey="pageviews"
          stroke="#2563eb"
          strokeWidth={2}
          fill="#3b82f6"
          fillOpacity={0.15}
          dot={false}
          activeDot={{ r: 6 }}
          name="Pageviews"
        />
        {showVisitors && (
          <Area
            type="monotone"
            dataKey="visitors"
            stroke="#7c3aed"
            strokeWidth={2}
            fill="#8b5cf6"
            fillOpacity={0.15}
            dot={false}
            activeDot={{ r: 6 }}
            name="Visitors"
          />
        )}
        {hasCrawlers && (
          <Area
            type="monotone"
            dataKey="crawlers"
            stroke="#9ca3af"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            fill="#9ca3af"
            fillOpacity={0.06}
            dot={false}
            activeDot={{ r: 5 }}
            name="Crawlers"
          />
        )}
        {hasBots && (
          <Area
            type="monotone"
            dataKey="bots"
            stroke="#f87171"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            fill="#f87171"
            fillOpacity={0.06}
            dot={false}
            activeDot={{ r: 5 }}
            name="Bots"
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default AnalyticsChart;
